<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  backdrop-filter: blur(3px);
  padding: 20px;
  font-family: 'Segoe UI', sans-serif;
}

.modal-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(13, 27, 62, 0.22);
  width: 100%;
  max-width: 480px;
  overflow: hidden;
}

/* ── Modal header ── */
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;

  .modal-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #0d1b3e;

    .title-icon {
      width: 30px;
      height: 30px;
      border-radius: 8px;
      background: #389ad4;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
    }
  }

  .btn-close {
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    border-radius: 6px;
    color: #7a8499;
    font-size: 15px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;

    &:hover { background: #fff; color: #c62828; }
  }
}

/* ── Modal body ── */
.modal-body {
  padding: 24px 24px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-row {
  display: flex;
  gap: 14px;

  .field-group { flex: 1; }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7a8499;

  .req { color: #e53e3e; margin-left: 2px; }
}

.field-input {
  height: 40px;
  border: 1.5px solid #dde2ec;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  color: #0d1b3e;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: 'Segoe UI', sans-serif;

  &::placeholder { color: #b0bac9; }
  &:focus { border-color: #389ad4; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
  &.has-error { border-color: #e53e3e; }
}

.field-select {
  height: 40px;
  border: 1.5px solid #dde2ec;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  color: #0d1b3e;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
  font-family: 'Segoe UI', sans-serif;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;

  &:focus { border-color: #389ad4; box-shadow: 0 0 0 3px rgba(56,154,212,0.1); }
  &.has-error { border-color: #e53e3e; }
}

.field-error {
  font-size: 11px;
  color: #e53e3e;
  margin-top: 2px;
}

/* ── Modal footer ── */
.modal-foot {
  display: flex;
  gap: 10px;
  padding: 16px 24px 24px;

  button {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: 1.5px solid;
    font-family: 'Segoe UI', sans-serif;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    &.btn-cancel {
      background: #fff;
      color: #4a5568;
      border-color: #dde2ec;
      &:hover { border-color: #389ad4; color: #389ad4; }
    }

    &.btn-save {
      background: #276749;
      color: #fff;
      border-color: #276749;
      &:hover { background: #1d5230; border-color: #1d5230; }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }
}
</style>

<template>
  <div class="modal-overlay" v-if="showModal" @click.self="close()">
    <div class="modal-card">

      <!-- Header -->
      <div class="modal-head">
        <div class="modal-title">
          <div class="title-icon">
            <i :class="edit ? 'fa fa-pencil-alt' : 'fa fa-user-plus'"></i>
          </div>
          {{ edit ? 'Update User' : 'Add User' }}
        </div>
        <button class="btn-close" @click="close()">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">

        <!-- First Name / Last Name -->
        <div class="field-row">
          <div class="field-group">
            <label class="field-label">First Name <span class="req">*</span></label>
            <input
              class="field-input"
              :class="{ 'has-error': errors['first_name'] }"
              type="text"
              placeholder="First name"
              v-model="userDetails.first_name"
            />
            <span class="field-error" v-if="errors['first_name']">{{ errors['first_name'][0] }}</span>
          </div>
          <div class="field-group">
            <label class="field-label">Last Name <span class="req">*</span></label>
            <input
              class="field-input"
              :class="{ 'has-error': errors['last_name'] }"
              type="text"
              placeholder="Last name"
              v-model="userDetails.last_name"
            />
            <span class="field-error" v-if="errors['last_name']">{{ errors['last_name'][0] }}</span>
          </div>
        </div>

        <!-- Email -->
        <div class="field-group">
          <label class="field-label">Email Address <span class="req">*</span></label>
          <input
            class="field-input"
            :class="{ 'has-error': errors['email'] }"
            type="email"
            placeholder="email@example.com"
            v-model="userDetails.email"
          />
          <span class="field-error" v-if="errors['email']">{{ errors['email'][0] }}</span>
        </div>

        <!-- Username (edit only) -->
        <div class="field-group" v-if="edit">
          <label class="field-label">Username <span class="req">*</span></label>
          <input
            class="field-input"
            :class="{ 'has-error': errors['username'] }"
            type="text"
            placeholder="Username"
            v-model="userDetails.username"
          />
          <span class="field-error" v-if="errors['username']">{{ errors['username'][0] }}</span>
        </div>

        <!-- Management Role -->
        <div class="field-group">
          <label class="field-label">Management Role</label>
          <select class="field-select" v-model="userDetails.roles">
            <option value="admin">Admin</option>
            <option value="staff">Staff</option>
          </select>
        </div>

        <!-- Live Scan Role (service provider only) -->
        <div class="field-group" v-if="currentUser.u.is_service_provider">
          <label class="field-label">Live Scan Role</label>
          <select class="field-select" v-model="userDetails.user_role" @change="onRoleChange($event)">
            <option value="">None</option>
            <option
              v-for="(citem, index) in getRoleData.data"
              :key="index"
              :value="citem.id"
            >{{ citem.title }}</option>
          </select>
        </div>

      </div>

      <!-- Footer -->
      <div class="modal-foot">
        <button class="btn-cancel" @click="close()">
          <i class="fa fa-times"></i> Cancel
        </button>
        <button
          v-if="edit"
          class="btn-save"
          ref="kt_update_user"
          @click="updateUser()"
        >
          <i class="fa fa-check"></i> Update
        </button>
        <button
          v-else
          class="btn-save"
          ref="kt_store_user"
          @click="storeUser()"
        >
          <i class="fa fa-check"></i> Save
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { UPDATE_USER_DETAIL, ADD_USER_DETAIL } from "@/core/services/store/actions.type";

export default {
  data() {
    return {
      errors: [],
      send_mail: true
    };
  },
  computed: {
    ...mapGetters(["currentUser", "getRoleData"])
  },
  props: {
    showModal: { type: Boolean, default: false },
    userDetails: {
      type: Object,
      default: function () { return []; }
    },
    edit: { type: Boolean, default: true },
    location_id: { type: Number, required: true }
  },
  methods: {
    close() {
      this.errors = [];
      this.$emit("closemodal", 0);
    },
    onRoleChange(event) {
      if (event.target.value == "null") {
        this.userDetails.user_role.value = null;
      }
    },
    storeUser() {
      const submitButton = this.$refs["kt_store_user"];
      submitButton.disabled = true;
      this.userDetails.name = this.userDetails.first_name + " " + this.userDetails.last_name;
      this.errors = [];
      let type = "";
      if (this.currentUser.u.is_service_provider) type = "sp";
      if (this.currentUser.u.is_admin) type = "admin";

      this.$store.dispatch(ADD_USER_DETAIL, {
        send_mail: this.send_mail,
        name: this.userDetails.name,
        first_name: this.userDetails.first_name,
        last_name: this.userDetails.last_name,
        email: this.userDetails.email,
        location_id: this.location_id,
        type,
        roles: this.userDetails.roles,
        username: this.userDetails.email,
        user_role: this.userDetails.user_role
      })
        .then(data => {
          submitButton.disabled = false;
          if (data.code == 202) {
            this.$swal({
              title: "Are you sure?",
              text: data.msg,
              buttons: true,
              dangerMode: true,
              showCancelButton: true,
              cancelButtonText: "No",
              confirmButtonText: "Yes"
            }).then(willDelete => {
              if (willDelete.isConfirmed) {
                this.send_mail = false;
                this.$store.dispatch(ADD_USER_DETAIL, {
                  send_mail: this.send_mail,
                  name: this.userDetails.name,
                  first_name: this.userDetails.first_name,
                  last_name: this.userDetails.last_name,
                  email: this.userDetails.email,
                  location_id: this.location_id,
                  type,
                  roles: this.userDetails.roles,
                  username: this.userDetails.email,
                  user_role: this.userDetails.user_role
                })
                  .then(data => {
                    this.$toastr.s(data.msg);
                    this.send_mail = true;
                    this.$emit("closemodal", 1);
                  })
                  .catch(err => {
                    this.errors = err;
                    this.send_mail = true;
                    submitButton.disabled = false;
                  });
              }
            });
          } else {
            this.$toastr.s(data.msg);
            this.send_mail = true;
            this.$emit("closemodal", 1);
          }
        })
        .catch(err => {
          this.errors = err;
          this.send_mail = true;
          submitButton.disabled = false;
        });
    },
    updateUser() {
      const submitButton = this.$refs["kt_update_user"];
      submitButton.disabled = true;
      this.userDetails.name = this.userDetails.first_name + " " + this.userDetails.last_name;
      this.errors = [];
      this.$store.dispatch(UPDATE_USER_DETAIL, {
        id: this.userDetails.id,
        name: this.userDetails.name,
        first_name: this.userDetails.first_name,
        last_name: this.userDetails.last_name,
        username: this.userDetails.username,
        email: this.userDetails.email,
        roles: this.userDetails.roles,
        user_role: this.userDetails.user_role
      })
        .then(data => {
          this.$toastr.s(data.msg);
          submitButton.disabled = false;
          this.$emit("closemodal", 1);
        })
        .catch(err => {
          this.errors = err.error;
          submitButton.disabled = false;
        });
    }
  }
};
</script>
